import { useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useCharges, useLinkOneTimeDiscount } from '../../hooks/useBilling';
import { useToast } from '../../providers/ToastProvider';
import { apiErrorMessage } from '../../utils/apiError';
import { StatusBadge } from './StatusBadge';
import { chargeNetCents } from './creditAllocation';
import { formatMoneyDollars } from '../../utils/money';
import type { ChargeListItem, OneTimeDiscountListItem } from '../../services/billing';
import { useEscapeKey } from '../../hooks/useEscapeKey';

// Only pending charges are offered: the backend refuses credit edits once a charge
// is processed or errored. A credit from a source charge scopes the list to its practice.

interface LinkOneTimeDiscountModalProps {
    oneTimeDiscount: OneTimeDiscountListItem;
    onClose: () => void;
}

function shortRef(id: string): string {
    return id.slice(0, 8).toUpperCase();
}

export function LinkOneTimeDiscountModal({
    oneTimeDiscount,
    onClose
}: LinkOneTimeDiscountModalProps) {
    const { showToast } = useToast();
    const { mutateAsync: link, isPending } = useLinkOneTimeDiscount();

    useEscapeKey(onClose, !isPending);
    const [selectedChargeId, setSelectedChargeId] = useState('');

    const baseParams = { page: 1, limit: 50, practiceId: oneTimeDiscount.practiceId ?? undefined };
    const { charges: openCharges, isLoading } = useCharges({ ...baseParams, status: 'pending' });

    const discountAmount = Number(oneTimeDiscount.amount);

    const handleSubmit = async () => {
        if (!selectedChargeId) return;
        try {
            await link({ id: oneTimeDiscount.id, chargeId: selectedChargeId });
            showToast('One-time discount linked to charge', 'success');
            onClose();
        } catch (error) {
            showToast(apiErrorMessage(error, 'Failed to link one-time discount'), 'error');
        }
    };

    const renderCharge = (charge: ChargeListItem) => {
        const isSelected = charge.id === selectedChargeId;
        const netTotal = chargeNetCents(charge) / 100;
        return (
            <label
                key={charge.id}
                className={`flex items-center gap-3 border rounded-lg px-4 py-3 cursor-pointer transition-colors ${
                    isSelected
                        ? 'border-gray-800 bg-gray-50 ring-2 ring-gray-100'
                        : 'border-gray-200 hover:bg-gray-50'
                }`}
            >
                <input
                    type="radio"
                    name="link-charge"
                    checked={isSelected}
                    onChange={() => setSelectedChargeId(charge.id)}
                    className="sr-only"
                />
                <span
                    className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 ${
                        isSelected ? 'border-gray-900' : 'border-gray-300'
                    }`}
                >
                    {isSelected && <span className="w-2 h-2 rounded-full bg-gray-900" />}
                </span>
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                        <span className="text-sm font-mono text-gray-700">
                            #{shortRef(charge.id)}
                        </span>
                        <StatusBadge status={charge.status} />
                    </div>
                    <p className="text-xs text-gray-500 truncate">
                        {oneTimeDiscount.practiceId ? '' : `${charge.practiceName} · `}
                        {charge.patientName && (
                            <span className="text-gray-700 font-medium">
                                {charge.patientName} ·{' '}
                            </span>
                        )}
                        {charge.productSummary ?? charge.description ?? 'No description'}
                    </p>
                </div>
                <div className="text-right shrink-0">
                    <p className="text-sm font-semibold text-gray-900 tabular-nums">
                        {formatMoneyDollars(netTotal)}
                    </p>
                    {isSelected && (
                        <p className="text-[11px] text-green-600 tabular-nums">
                            {formatMoneyDollars(Math.max(0, netTotal - discountAmount))} after
                            one-time discount
                        </p>
                    )}
                </div>
            </label>
        );
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-[520px] max-h-[85vh] flex flex-col">
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 shrink-0">
                    <div>
                        <h2 className="text-lg font-bold text-gray-800">
                            Link one-time discount #{shortRef(oneTimeDiscount.id)}
                        </h2>
                        <p className="text-xs text-gray-500 mt-0.5">
                            <span className="font-semibold text-gray-700">
                                {formatMoneyDollars(oneTimeDiscount.amount)}
                            </span>
                            {oneTimeDiscount.practiceName && <> · {oneTimeDiscount.practiceName}</>}
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-800 transition-colors"
                        aria-label="Close"
                    >
                        <XMarkIcon className="h-5 w-5" />
                    </button>
                </div>

                <div className="overflow-y-auto flex-1 p-6 flex flex-col gap-3">
                    <span className="text-[13px] font-semibold text-gray-700">
                        {oneTimeDiscount.practiceId
                            ? 'Open charges for this practice'
                            : 'Open charges'}
                    </span>
                    {isLoading ? (
                        <div className="flex items-center justify-center gap-2 text-gray-400 py-8">
                            <div className="w-4 h-4 rounded-full border-2 border-gray-300 border-t-gray-600 animate-spin" />
                            <span className="text-sm">Loading charges…</span>
                        </div>
                    ) : openCharges.length === 0 ? (
                        <p className="text-sm text-gray-500 py-6 text-center">
                            No open charges{oneTimeDiscount.practiceId ? ' for this practice' : ''}.
                            One-time discounts can only be linked to pending charges.
                        </p>
                    ) : (
                        <div className="flex flex-col gap-2">{openCharges.map(renderCharge)}</div>
                    )}
                </div>

                <div className="flex justify-end items-center gap-3 px-6 py-4 border-t border-gray-200 shrink-0">
                    <button
                        type="button"
                        onClick={onClose}
                        className="bg-white text-gray-800 border border-gray-300 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors font-semibold text-sm"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={() => {
                            void handleSubmit();
                        }}
                        disabled={!selectedChargeId || isPending}
                        className="bg-gray-800 text-white rounded-lg px-4 py-2 text-sm font-semibold hover:bg-gray-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isPending ? 'Linking…' : 'Link one-time discount'}
                    </button>
                </div>
            </div>
        </div>
    );
}
